import { hasValue, styles } from "@repo/helpers";
import { keyPair } from "@repo/models";
import { useCallback, useMemo } from "react";

import { DdlValue } from "../../dropDownList/types";
import { InputComponent } from "../input";
import { Loading } from "../loading";
import style from "./style.module.scss";
import { MarkupProps } from "./types";

export default function MarkupPanel(props: MarkupProps) {
	const {
		values,
		clearButton,
		isMulti,
		valueChanged,
		size,
		clearable,
		floatingHeight,
		loaded = true,
		textProp = "label",
		valueProp = "id",
		hideInputGroupClass = true,
		...inputProps
	} = props;

	function renderPanels() {
		return (
			<Loading tag="h5" right loading={!loaded}>
				{values.map(renderPanel)}
			</Loading>
		);

		function renderPanel(option: keyPair) {
			return <SinglePanel {...props} option={option} key={option.id} />;
		}
	}

	const sizeStyle = style[size] ?? style.large;
	const className = styles(style.panelContainer, !floatingHeight && sizeStyle);
	return (
		<InputComponent {...inputProps} hideAddon hideInputGroupClass={hideInputGroupClass} className={className}>
			{renderPanels()}
		</InputComponent>
	);
}

function SinglePanel(props: SinglePanelProps) {
	const { option, valueChanged, value, isMulti, readOnly, disabled, clearable, id } = props;
	const panelValue = option.id as string;
	const label = option.value;
	const derivedLabel = typeof label === "string" ? <span>{label}</span> : label;

	const selected = useMemo(() => {
		if (!hasValue(value)) return false;
		const ddlOption = value as DdlValue;
		if (isMulti) return (value as DdlValue[]).map((v) => v.value).includes(panelValue);
		return ddlOption?.value === panelValue;
	}, [value, panelValue, isMulti]);

	const tryClear = useCallback(
		function () {
			if (selected && clearable && !isMulti) valueChanged(null);
		},
		[selected, clearable, isMulti, valueChanged],
	);

	return (
		<div className={style.labelContainer}>
			<label
				className={styles(
					style.container,
					selected && style.selected,
					style.containerBorder,
					disabled && style.disabled,
				)}
			>
				<input
					type={isMulti ? "checkbox" : "radio"}
					name={id}
					value={panelValue}
					checked={selected}
					readOnly={readOnly}
					disabled={disabled}
					onChange={() => valueChanged(panelValue)}
					onClick={tryClear}
				/>
				{derivedLabel}
			</label>
		</div>
	);
}

interface SinglePanelProps extends MarkupProps {
	option: keyPair<string>;
}
